import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { OnboardingTourProvider } from "@/contexts/OnboardingTourContext";
import LanguageSyncRouter from "@/components/LanguageSyncRouter";
import NotFound from "./pages/NotFound";

// TooltipProvider lazy-loaded — pulls @radix-ui/react-tooltip + @floating-ui
// (~5 KB gzip) which the landing page never uses. Suspense fallback renders
// children directly so nothing breaks while the chunk streams in.
const TooltipProvider = lazy(() =>
  import("@/components/ui/tooltip").then((m) => ({ default: m.TooltipProvider }))
);

// Dashboard shell + ProtectedRoute lazy-loaded — they pull DashboardHeader,
// NotificationBell, dropdown-menu, dialog, avatar, etc. (≈30 KB gzip) which
// landing visitors never need.
const ProtectedRoute = lazy(() => import("@/components/ProtectedRoute"));
const DashboardLayout = lazy(() => import("@/components/DashboardLayout"));

// Heavy leaf UI deferred — not needed for first paint of the landing page.
// Suspense renders nothing while these stream in.
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const OnboardingTourOverlay = lazy(() => import("@/components/OnboardingTourOverlay"));

// Landing page is lazy-loaded so its lucide icons + 750-line tree leave the
// main bundle. The HTML shell paints instantly while the chunk streams in.
const Index = lazy(() => import("./pages/Index"));

// Lazy-loaded routes
const SignUp = lazy(() => import("./pages/SignUp"));
const Login = lazy(() => import("./pages/Login"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Portal = lazy(() => import("./pages/Portal"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const Contact = lazy(() => import("./pages/Contact"));
const ClientPortalForAccountants = lazy(() => import("./pages/ClientPortalForAccountants"));
const DashboardHome = lazy(() => import("./pages/DashboardHome"));
const Clients = lazy(() => import("./pages/Clients"));
const Requests = lazy(() => import("./pages/Requests"));
const NewRequest = lazy(() => import("./pages/NewRequest"));
const RequestDetail = lazy(() => import("./pages/RequestDetail"));
const ScheduleDetail = lazy(() => import("./pages/ScheduleDetail"));
const Settings = lazy(() => import("./pages/Settings"));
const Billing = lazy(() => import("./pages/Billing"));
const Reporting = lazy(() => import("./pages/Reporting"));
const Templates = lazy(() => import("./pages/Templates"));

const Admin = lazy(() => import("./pages/Admin"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminUserDetail = lazy(() => import("./pages/AdminUserDetail"));
const AdminPricing = lazy(() => import("./pages/AdminPricing"));
const AdminAnnouncements = lazy(() => import("./pages/AdminAnnouncements"));
const AdminReports = lazy(() => import("./pages/AdminReports"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminStatus = lazy(() => import("./pages/AdminStatus"));
const AdminReminders = lazy(() => import("./pages/AdminReminders"));
const AdminEvents = lazy(() => import("./pages/AdminEvents"));

const queryClient = new QueryClient();

// Render children directly while TooltipProvider chunk streams in. Tooltips
// inside dashboard routes won't function until the provider mounts (a few ms),
// but no Tooltip is used above-the-fold on landing so this is invisible.
const LazyTooltipProvider = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<>{children}</>}>
    <TooltipProvider>{children}</TooltipProvider>
  </Suspense>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LazyTooltipProvider>
      <Suspense fallback={null}>
        <Toaster />
        <Sonner />
      </Suspense>
      <BrowserRouter>
        <AuthProvider>
          <OnboardingTourProvider>
          <LanguageSyncRouter />
          <Suspense fallback={null}>
            <OnboardingTourOverlay />
          </Suspense>
          <Suspense fallback={<div className="min-h-screen" />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/fr" element={<Index />} />
            <Route path="/fr/signup" element={<SignUp />} />
            <Route path="/fr/login" element={<Login />} />
            <Route path="/fr/contact" element={<Contact />} />
            <Route path="/fr/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/fr/terms" element={<Terms />} />
            <Route path="/fr/blog" element={<Blog />} />
            <Route path="/fr/blog/:slug" element={<BlogPost />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/portal/:token" element={<Portal />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/client-portal-software-for-accountants" element={<ClientPortalForAccountants />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <DashboardHome />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/clients"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Clients />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/requests"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Requests />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/requests/new"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <NewRequest />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/requests/schedule/:id"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ScheduleDetail />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/requests/:id"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <RequestDetail />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/templates"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Templates />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/recurring"
              element={<Navigate to="/dashboard/requests?filter=recurring" replace />}
            />
            <Route
              path="/dashboard/reporting"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Reporting />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/settings"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Settings />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/billing"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Billing />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Admin />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
            <Route path="/admin/users" element={<ProtectedRoute><DashboardLayout><AdminUsers /></DashboardLayout></ProtectedRoute>} />
            <Route path="/admin/users/:id" element={<ProtectedRoute><DashboardLayout><AdminUserDetail /></DashboardLayout></ProtectedRoute>} />
            <Route path="/admin/pricing" element={<ProtectedRoute><DashboardLayout><AdminPricing /></DashboardLayout></ProtectedRoute>} />
            <Route path="/admin/announcements" element={<ProtectedRoute><DashboardLayout><AdminAnnouncements /></DashboardLayout></ProtectedRoute>} />
            <Route path="/admin/reports" element={<ProtectedRoute><DashboardLayout><AdminReports /></DashboardLayout></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute><DashboardLayout><AdminSettings /></DashboardLayout></ProtectedRoute>} />
            <Route path="/admin/status" element={<ProtectedRoute><DashboardLayout><AdminStatus /></DashboardLayout></ProtectedRoute>} />
            <Route path="/admin/reminders" element={<ProtectedRoute><DashboardLayout><AdminReminders /></DashboardLayout></ProtectedRoute>} />
            <Route path="/admin/events" element={<ProtectedRoute><DashboardLayout><AdminEvents /></DashboardLayout></ProtectedRoute>} />
            <Route path="/a-9k2m" element={<Navigate to="/admin" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </OnboardingTourProvider>
        </AuthProvider>
      </BrowserRouter>
    </LazyTooltipProvider>
  </QueryClientProvider>
);

export default App;
